import type { ReactNode } from 'react';
import type { DomainRule } from '@/api/apps-setup';
import type { FactoryDomainInput } from '@/api/factory';

// App Factory's own domain: the stub's repo type decides (Repos Setup tab).
// A type that requires one always gets it, one that sends none never does,
// an optional one only when ticked here — an app without our SDK has no API
// host to put on a domain. Without a domain the privacy policy is the
// standalone generator's page, with the address typed here or else the Gmail
// of the console the run publishes in.

export interface DomainChoiceValue { ownDomain: boolean; email: string }
export const NO_DOMAIN_CHOICE: DomainChoiceValue = { ownDomain: false, email: '' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether the run goes without an own domain — the server's skipsDomain. */
export function skipsDomain(rule: DomainRule | null, v: DomainChoiceValue, existingDomain: string | null = null): boolean {
  if (existingDomain || rule === 'req') return false;
  return rule === 'off' || !v.ownDomain;
}

/** The fields createFactoryRun takes. */
export function domainInput(rule: DomainRule | null, v: DomainChoiceValue): FactoryDomainInput {
  const email = v.email.trim();
  return { own_domain: rule !== 'off' && v.ownDomain, ...(email ? { contact_email: email } : {}) };
}

/** Why the run cannot start yet, or null. */
export function domainChoiceProblem(
  rule: DomainRule | null, v: DomainChoiceValue, opts: { publishing: boolean; hasPolicy?: boolean; existingDomain?: string | null },
): string | null {
  if (!skipsDomain(rule, v, opts.existingDomain ?? null)) return null;
  const email = v.email.trim();
  if (email && !EMAIL_RE.test(email)) return 'The contact email does not look like an address';
  if (!email && !opts.publishing && !opts.hasPolicy) return 'Type the privacy policy’s contact email';
  return null;
}

export function FactoryDomainChoice({ rule, typeName, value, onChange, publishing, hasPolicy = false, existingDomain = null, className = '' }: {
  rule: DomainRule | null;
  typeName: string | null;
  value: DomainChoiceValue;
  onChange: (v: DomainChoiceValue) => void;
  /** The run publishes on a console of ours: its Gmail is the fallback address. */
  publishing: boolean;
  /** An existing app's policy stays when no address is typed. */
  hasPolicy?: boolean;
  existingDomain?: string | null;
  className?: string;
}) {
  const skipped = skipsDomain(rule, value, existingDomain);
  const type = typeName ?? 'This stub';
  let head: ReactNode;
  if (existingDomain) {
    head = <span>Own domain: keeps <span className="font-mono">{existingDomain}</span>, the privacy policy stays on it.</span>;
  } else if (rule === 'req') {
    head = <span>Own domain: bought when the app is created ({type} requires one), the privacy policy and its mailbox go on it.</span>;
  } else if (rule === 'off') {
    head = <span>No own domain — {type} builds without one.</span>;
  } else {
    head = (
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-0.5" checked={value.ownDomain} onChange={(e) => onChange({ ...value, ownDomain: e.target.checked })} />
        <span>
          Buy an own domain for the privacy policy and the SDK’s API host{' '}
          ({typeName ? `optional for ${typeName}` : 'the stub has no type'} — leave off for an app without our SDK)
        </span>
      </label>
    );
  }
  const fallback = hasPolicy
    ? 'empty keeps the policy the app has'
    : publishing ? 'empty takes the Gmail of the console it is published in' : 'required — nobody else’s address is known';

  return (
    <div className={`text-xs text-content-subtle space-y-2 ${className}`}>
      {head}
      {skipped && (
        <div className="flex flex-wrap items-center gap-2">
          <span>Privacy policy on the shared generator, contact</span>
          <input
            className="input text-xs py-1 w-64"
            type="email"
            placeholder="contact@example.com"
            value={value.email}
            onChange={(e) => onChange({ ...value, email: e.target.value })}
          />
          <span>({fallback})</span>
        </div>
      )}
    </div>
  );
}
